import { useEffect, useRef, useState } from 'react';
import { cn } from '../../lib/cn';

export function DropdownMenu({
  trigger,
  items = [],
  align = 'right',
  className,
  menuClassName,
}) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    function onClickOutside(e) {
      if (
        !triggerRef.current?.contains(e.target) &&
        !menuRef.current?.contains(e.target)
      ) {
        setOpen(false);
      }
    }
    function onEsc(e) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onClickOutside);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onClickOutside);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  return (
    <div className={cn('relative inline-flex', className)}>
      <span
        ref={triggerRef}
        onClick={() => setOpen((v) => !v)}
        className="inline-flex"
      >
        {trigger}
      </span>
      {open && (
        <div
          ref={menuRef}
          className={cn(
            'absolute top-full z-50 mt-2 min-w-[180px] overflow-hidden rounded-xl py-1',
            align === 'right' ? 'right-0' : 'left-0',
            menuClassName
          )}
          style={{
            background: 'var(--dash-surface)',
            border: '1px solid var(--dash-border-strong)',
            boxShadow: '0 24px 48px -12px rgba(0,0,0,0.35)',
            animation: 'dropdown-fade 0.18s ease-out',
          }}
        >
          {items.map((item, i) => {
            if (item.separator) {
              return (
                <div
                  key={`sep-${i}`}
                  className="my-1 h-px"
                  style={{ background: 'var(--dash-border)' }}
                />
              );
            }
            const Icon = item.icon;
            return (
              <button
                key={item.key || i}
                type="button"
                onClick={() => {
                  item.onClick?.();
                  setOpen(false);
                }}
                disabled={item.disabled}
                className={cn(
                  'flex w-full items-center gap-2.5 px-3 py-2 text-left text-xs font-medium transition-colors',
                  item.disabled && 'cursor-not-allowed opacity-50'
                )}
                style={{
                  color: item.danger
                    ? '#d61f3f'
                    : 'var(--dash-text)',
                }}
                onMouseEnter={(e) => {
                  if (!item.disabled)
                    e.currentTarget.style.background =
                      'var(--dash-surface-hover)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                }}
              >
                {Icon && <Icon size={13} strokeWidth={2.2} />}
                <span className="flex-1 truncate">{item.label}</span>
              </button>
            );
          })}
          <style>{`
            @keyframes dropdown-fade {
              from { opacity: 0; transform: translateY(-4px); }
              to { opacity: 1; transform: translateY(0); }
            }
          `}</style>
        </div>
      )}
    </div>
  );
}
